import React, { Component } from 'react';
import { connect } from 'react-redux';
import withStyles from 'react-jss';
import { isEmpty, every } from 'lodash';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import classNames from 'classnames';

import PlaceholderCard from './PlaceholderCard';
import { getEcapState } from '../../../../store/selectors';
import CloseIcon from '../../../utility/CloseIcon';
import ClassPopUp from './ClassPopUp';
import {
  getRequiredClassesByAcadPlan,
  getYearListItemStyle,
  isRecommendedClass,
  getAriaLabelForClass,
} from './dragDropUtil';
import { middle } from '../../../../copy/ecap/classesSetupCopy.json';
import { coreSubjectsEn } from '../../../../copy/ecap/classesSetupStrings';

const customInputStyles = {
  reqText: () => ({
    color: 'black',
    padding: '16px',
  }),

  customClass: () => ({
    color: 'black',

    '&::placeholder': {
      color: 'black',
    },
  }),
};

class CoreClassPerYearDroppable extends Component {
  get isFull() {
    const classSlots = this.props.placedClasses || [];

    return (
      classSlots.length === 2 && classSlots.every((classValue) => classValue)
    );
  }

  get isElective() {
    return ['Additional Electives', 'Electives'].includes(this.propsSubject);
  }

  get propsSubject() {
    return this.props.subject;
  }

  get propsIndex() {
    return this.props.index;
  }

  get droppableId() {
    return `${this.propsSubject}_${this.propsIndex}`;
  }

  get academicAreaIdMatch() {
    if (this.props.currentDraggingClass) {
      return (
        this.props.currentDraggingClass.academic_area_id === this.rowIndex + 1
      );
    }
    return null;
  }

  get currentDraggingClass() {
    if (this.props.currentDraggingClass) return this.props.currentDraggingClass;
    return null;
  }

  get isDropDisabled() {
    if (this.isElective) return false;
    if (
      this.currentDraggingClass &&
      this.currentDraggingClass.academic_area_id === 'Custom' &&
      !this.isFull
    )
      return false;
    if (!this.academicAreaIdMatch || this.isFull) return true;
    return false;
  }

  get droppableSpots() {
    if (
      isEmpty(this.props.placedClasses) ||
      every(this.props.placedClasses, (placedClass) => placedClass === null)
    ) {
      if (isEmpty(this.requiredClassesPerDroppable)) return ['Elective'];

      return this.requiredClassesPerDroppable.map(
        (eachClass) => eachClass.requirement_title || eachClass.titleEn
      );
    }

    const firstNullIndex = this.props.placedClasses.findIndex(
      (placedClass) => !placedClass
    );

    if (firstNullIndex >= 0) {
      return this.handleNullPlaceholder(firstNullIndex);
    }

    return this.props.placedClasses;
  }

  get acadPlanClasses() {
    return this.props.acadPlanClasses || [];
  }

  get placedClasses() {
    return this.props.placedClasses || [];
  }

  get yearColumnIndex() {
    // incremented to correspond to class object data for year/track_term
    return this.propsIndex + 1;
  }

  get rowIndex() {
    return Object.values(coreSubjectsEn).indexOf(this.propsSubject);
  }

  get requiredClassesPerDroppable() {
    return (
      this.props.hsClasses
        ?.filter((eachClass) => eachClass.year === this.yearColumnIndex)
        // Need to add 1 to rowindex because of 0 indexing
        .filter((eachClass) => eachClass.academic_area_id === this.rowIndex + 1)
    );
  }

  handleNullPlaceholder = (firstNullIndex) => {
    if (this.requiredClassesPerDroppable) {
      const placedClassesCopy = JSON.parse(
        JSON.stringify(this.props.placedClasses)
      );

      placedClassesCopy[firstNullIndex] =
        this.requiredClassesPerDroppable[firstNullIndex].titleEn;

      return placedClassesCopy;
    }
  };

  checkIsRecommended = (hsClass) => {
    const suggestedPlaceholders = [];

    this.requiredClassesPerDroppable.forEach((placeholder) =>
      suggestedPlaceholders.push(placeholder?.titleEn?.toLowerCase())
    );

    return isRecommendedClass(
      hsClass,
      suggestedPlaceholders,
      this.props.requiredClassesByAcadPlanBySubject
    );
  };

  // RENDER

  renderPlaceholderCard = (classTitle, provided, dropSnapshot) => {
    return (
      <PlaceholderCard
        key={`${this.propsSubject}-${this.propsIndex}-${classTitle}`}
        placeHolderText={classTitle}
        isDraggingOver={dropSnapshot.isDraggingOver}
        style={{
          borderColor: dropSnapshot.isDraggingOver
            ? '#009dff'
            : this.props.tenant.primary,
        }}
      />
    );
  };

  getDroppedCount = () => {
    if (
      this.props.ecap.plan.classes_matched &&
      this.props.ecap.plan.classes_matched[
        `${this.propsSubject}_${this.propsIndex}`
      ]
    ) {
      return this.props.ecap.plan.classes_matched[
        `${this.propsSubject}_${this.propsIndex}`
      ].length;
    }
  };

  determineYearColumnClass = (isDraggingOver, twoPlaceholders) => {
    if (
      this.getDroppedCount() === 2 ||
      (this.getDroppedCount() === 1 && isDraggingOver) ||
      twoPlaceholders
    ) {
      return 'year-column-extended';
    }

    if (this.getDroppedCount() < 2) {
      return 'year-column';
    }

    return 'year-column';
  };

  getCloseIconAriaLabel = (subject, index, className) => {
    return `Remove ${className} from ${subject} year ${index + 1}`;
  };

  renderDraggableCard = (hsClass, spotIndex, provided, dropSnapshot) => {
    const droppedCount = this.getDroppedCount();
    const isCustomClass = hsClass && hsClass.academic_area_id === 'Custom';
    const draggableId = `placed-${
      hsClass.requirement_title || hsClass.titleEn
    }`;

    const styleVisible = () => {
      document.getElementById(
        `${this.propsSubject}_${this.propsIndex}_${hsClass.requirement_title}`
      ).style.display = 'block';
    };

    const styleHidden = () => {
      document.getElementById(
        `${this.propsSubject}_${this.propsIndex}_${hsClass.requirement_title}`
      ).style.display = 'none';
    };

    /* eslint-disable react/jsx-props-no-spreading */
    return (
      <>
        <div className="single-draggable-close-button-container">
          <Draggable
            key={hsClass.requirement_title}
            draggableId={draggableId}
            index={spotIndex}
            // DEVNOTE: leave this in case we decide to revisit the ability to allow custom classes be redragged
            // isDragDisabled={!isCustomClass}
            isDragDisabled
          >
            {(dragProvided, snapshot) => (
              <>
                {snapshot.isDragging ? null : (
                  <CloseIcon
                    ariaLabel={this.getCloseIconAriaLabel(
                      this.props.subject,
                      this.props.index,
                      hsClass.requirement_title
                    )}
                    tenant={this.props.tenant}
                    onClickParent={() => this.props.onClickRemove(spotIndex)}
                    draggableId={this.propsSubject}
                  />
                )}
                <div
                  ref={dragProvided.innerRef}
                  {...dragProvided.draggableProps}
                  {...dragProvided.dragHandleProps}
                  onMouseOver={styleVisible}
                  onFocus={styleVisible}
                  onMouseOut={styleHidden}
                  onBlur={styleHidden}
                  tabIndex={0}
                  role="button"
                  aria-label={getAriaLabelForClass(
                    this.requiredClassesPerDroppable[spotIndex]
                      ? [this.requiredClassesPerDroppable[spotIndex]?.titleEn]
                      : [],
                    hsClass
                  )}
                  style={getYearListItemStyle(
                    this.checkIsRecommended(hsClass) ? 'green' : 'gold',
                    dragProvided,
                    droppedCount,
                    dropSnapshot.isDraggingOver
                  )}
                >
                  <div
                    id={`${this.propsSubject}_${this.propsIndex}_${hsClass.requirement_title}`}
                    style={{ display: 'none' }}
                    className="bubble-class-indicator sb9-class-indicator"
                  >
                    <ClassPopUp
                      hsClass={hsClass}
                      showCautionIcon={this.checkIsRecommended(hsClass)}
                      recommendedClass={
                        this.requiredClassesPerDroppable[spotIndex] &&
                        this.requiredClassesPerDroppable[spotIndex].titleEn
                      }
                    />
                  </div>
                  {isCustomClass ? (
                    <input
                      type="text"
                      aria-hidden="true"
                      className={classNames(
                        'truncated-class-text custom-year-column',
                        this.props.classes.customClass
                      )}
                      placeholder={hsClass.titleEn}
                      onKeyDown={({ key, target: { value } }) => {
                        if (['Enter'].includes(key) && value !== '')
                          this.props.onRename(spotIndex, value);
                      }}
                      onBlur={({ target: { value } }) => {
                        if (value !== '') this.props.onRename(spotIndex, value);
                      }}
                    />
                  ) : (
                    <p
                      className={[
                        'truncated-class-text',
                        this.props.classes.reqText,
                      ].join(' ')}
                    >
                      {hsClass.requirement_title}
                    </p>
                  )}
                </div>
              </>
            )}
          </Draggable>
        </div>
        {this.getDroppedCount() === 1 && dropSnapshot.isDraggingOver ? (
          <div
            style={getYearListItemStyle(
              '#009dff',
              false,
              droppedCount,
              dropSnapshot.isDraggingOver
            )}
          />
        ) : null}
      </>
    );
    /* eslint-enable react/jsx-props-no-spreading */
  };

  render() {
    return (
      <Droppable
        key={`core-class-droppable-${this.propsSubject}_${this.propsIndex}`}
        droppableId={this.droppableId}
        isDropDisabled={this.isDropDisabled}
      >
        {(provided, dropSnapshot) => (
          <div
            key={`droppable-div-${this.propsIndex}`}
            className={this.determineYearColumnClass(
              dropSnapshot.isDraggingOver,
              this.droppableSpots.length > 1
            )}
            ref={provided.innerRef}
            style={this.props.style}
          >
            {this.props.children}
            {this.droppableSpots.map((spotText, spotIndex) => {
              const placedClass = this.placedClasses[spotIndex];

              /* eslint-disable react/no-array-index-key */
              return (
                <React.Fragment key={`class-${this.propsSubject}-${spotIndex}`}>
                  {placedClass
                    ? this.renderDraggableCard(
                        placedClass,
                        spotIndex,
                        provided,
                        dropSnapshot
                      )
                    : this.renderPlaceholderCard(
                        spotText,
                        provided,
                        dropSnapshot
                      )}
                </React.Fragment>
              );
              /* eslint-enable react/no-array-index-key */
            })}
          </div>
        )}
      </Droppable>
    );
  }
}

const mapStateToProps = (state) => {
  const onboarding = state.onboarding;
  const tenant = state.tenant;
  const ecap = getEcapState(state);

  return { onboarding, tenant, ecap };
};

export default connect(
  mapStateToProps,
  {}
)(withStyles(customInputStyles)(CoreClassPerYearDroppable));
